/* eslint-disable local-rules/set-state-in-use-effect */
import {useTranslation} from '@aboutyou/i18n';
import {
  useCurrentPageType,
  useHistory,
  usePathname,
  useSearch,
} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import {doAsync} from '@aboutyou/utils';
import React, {useEffect, useRef, useState} from 'react';
import {useHistoryReplaceWithSwipePrevention} from '../../../backend-services/CouponLandingPage/ProductStream/usePreventProductTilesSwipeAnimation';
import {updateUrlParams} from '../../../shared-deprecated/updateUrlParams';
import {CouponDeepLinkActivationProps} from '../../CouponDeepLinkActivation/CouponDeepLinkActivation';
import {getCouponPromotionId} from '../../Coupons/getCouponPromotionId';
import {isAppOnlyCoupon} from '../../Coupons/isAppOnlyCoupon';
import {useActivatedCouponState} from '../../Coupons/useActivatedCouponState';
import {useEntryUrlCrmCouponTokenParam} from '../../Coupons/useCrmCouponTokenParam';
import {useUnlockedCouponTokens} from '../../Coupons/useUnlockedCouponTokens';
import {CRM_COUPON_TOKEN_PARAM} from '../../Coupons/utils/crmCouponTokenParam';
import {useProductIdFromSlug} from '../../ProductUrl';
import {ErrorNotificationContent} from '../components/ErrorNotificationContent';
import {PausedNotificationContent} from '../components/PausedNotificationContent';
import {SoftSuccessNotificationContent} from '../components/SoftSuccessNotificationContent';
import {useRetroactivelyApplyCouponToProduct} from './useRetroactivelyApplyCouponToProduct';
import {useUnlockCouponInfoCallback} from './useUnlockCouponInfoCallback';

type CouponActivationResponse = Pick<
  CouponDeepLinkActivationProps,
  'status' | 'headline' | 'subline' | 'coupon' | 'alreadyUnlocked'
>;

const DELAY_BEFORE_TRANSITION_TO_NEXT_STATE = 2000;
const DELAY_BEFORE_ADP_ACTIVATION_HEADER_REMOVED = 3000;
const AUTO_CLOSE_TIMEOUT = 6000;

export const useUnlockCouponInfo = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const trackFeature = useTrackFeature();
  const showToast = useShowToast();

  const [isOpen, setIsOpen] = useState(true);
  const [couponActivationResponse, setCouponActivationResponse] =
    useState<CouponActivationResponse>({status: 'loading'});

  const {unlockCouponInfoCallback} = useUnlockCouponInfoCallback();

  const setNextState = (state: CouponActivationResponse) => {
    setTimeout(() => {
      setCouponActivationResponse(state);
    }, DELAY_BEFORE_TRANSITION_TO_NEXT_STATE);
  };

  const cancelTimeoutIdRef = useRef<NodeJS.Timeout>(undefined);

  const entryPageType = useRef(useCurrentPageType()).current;
  const couponToken = useEntryUrlCrmCouponTokenParam(); // keeping ref since we dont want to immediately unmount the component when a redirect happens from within the UnlockCoupon datafetcher
  const {addToUnlockedCouponTokens} = useUnlockedCouponTokens();
  const removeCrmCouponParam = useRemoveCrmCouponParam();
  const replaceHistoryWithSwipePrevention =
    useHistoryReplaceWithSwipePrevention();

  const productIdFromSlug = useProductIdFromSlug();
  const {setActivatedCoupon, setUnlockedCouponHeadline} =
    useActivatedCouponState(productIdFromSlug);

  const retroactivelyApplyCouponToProduct =
    useRetroactivelyApplyCouponToProduct({productId: productIdFromSlug});

  const setActivationHeaderAndClearDelayed = (headline: string) => {
    setUnlockedCouponHeadline(headline);

    // the success message is cleared after a delay, so that we show the expiration countdown instead in place of the success message
    // This is only applicable on mobile. On desktop we show the success message until the user switches to another coupon/page
    setTimeout(() => {
      if (process.env.STACK === 'mobile') {
        setUnlockedCouponHeadline('');
      }
    }, DELAY_BEFORE_ADP_ACTIVATION_HEADER_REMOVED);
  };

  const softRemoveCrmCouponParam = () => {
    // we should remove the cpn query param all pages except the coupon activation page, so that when the user refreshes the page, the coupon is not unlocked again
    if (entryPageType !== 'couponLandingPage') {
      removeCrmCouponParam();
    }
  };

  const getUrlWithParamsButCrmCoupon = useGetUrlWithParamsButCrmCoupon();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    unlockCouponInfoCallback()
      .then(couponUnlockInfo => {
        switch (couponUnlockInfo.type?.$case) {
          case 'success': {
            const success = couponUnlockInfo.type.success;

            addToUnlockedCouponTokens({
              couponToken: couponToken!,
              paused: false,
            });
            softRemoveCrmCouponParam();

            if (entryPageType === 'articleDetail') {
              doAsync(() => {
                if (isAppOnlyCoupon(success.coupon)) {
                  setActivationHeaderAndClearDelayed(success.headline);
                } else {
                  // activate the coupon only if its not an app only coupon
                  setActivatedCoupon(success.coupon);
                  setActivationHeaderAndClearDelayed(success.headline);

                  retroactivelyApplyCouponToProduct({
                    couponPromotionId: getCouponPromotionId(success.coupon)!,
                  });
                }
              });
            } else {
              setNextState({
                status: 'success',
                headline: success.headline,
                subline: success.subline,
                coupon: success.coupon,
                alreadyUnlocked:
                  (entryPageType === 'category' ||
                    entryPageType === 'brandShop') &&
                  success.alreadyUnlocked,
              });
            }

            if (entryPageType === 'couponLandingPage') {
              const couponPromotionId = getCouponPromotionId(success.coupon);

              const urlWithoutCrmCouponParam = getUrlWithParamsButCrmCoupon(
                `/cs/${couponPromotionId}`,
              );

              if (couponPromotionId) {
                setTimeout(() => {
                  replaceHistoryWithSwipePrevention(urlWithoutCrmCouponParam);
                }, AUTO_CLOSE_TIMEOUT);
              }
            }

            trackFeature({
              eventType: 'impression',
              ...couponUnlockInfo.type.success.featureTracker,
            });

            break;
          }

          case 'paused': {
            const paused = couponUnlockInfo.type.paused;

            addToUnlockedCouponTokens({
              couponToken: couponToken!,
              paused: true,
            });
            softRemoveCrmCouponParam();

            if (entryPageType === 'articleDetail') {
              showToast({
                type: 'SUCCESS',
                content: (
                  <PausedNotificationContent headline={paused.headline} />
                ),
              });
            } else {
              setNextState({
                status: 'paused',
                headline: paused.headline,
                subline: paused.subline,
                coupon: paused.coupon,
              });
            }

            setTimeout(() => {
              if (paused.redirect?.url) {
                const urlWithoutCrmCouponParam = getUrlWithParamsButCrmCoupon(
                  paused.redirect.url,
                );

                replaceHistoryWithSwipePrevention(urlWithoutCrmCouponParam);
              }
            }, AUTO_CLOSE_TIMEOUT);

            trackFeature({
              eventType: 'impression',
              ...couponUnlockInfo.type.paused.featureTracker,
            });

            break;
          }

          case 'softSuccessNotification': {
            const softSuccessNotification =
              couponUnlockInfo.type.softSuccessNotification;

            addToUnlockedCouponTokens({
              couponToken: couponToken!,
              paused: false,
            });
            softRemoveCrmCouponParam();

            showToast({
              type: 'SUCCESS',
              content: (
                <SoftSuccessNotificationContent
                  headline={softSuccessNotification.headline}
                  cta={softSuccessNotification.cta}
                />
              ),
            });

            trackFeature({
              eventType: 'impression',
              ...couponUnlockInfo.type.softSuccessNotification.featureTracker,
            });

            break;
          }

          case 'error': {
            const error = couponUnlockInfo.type.error;

            if (entryPageType === 'articleDetail') {
              showToast({
                type: 'WARNING',
                content: (
                  <ErrorNotificationContent headline={t(error.headline)} />
                ),
              });
            } else {
              setNextState({
                status: 'error',
                headline: error.headline,
                subline: error.subline,
              });
            }

            setTimeout(() => {
              if (error.redirect?.url) {
                const urlWithoutCrmCouponParam = getUrlWithParamsButCrmCoupon(
                  error.redirect.url,
                );

                replaceHistoryWithSwipePrevention(urlWithoutCrmCouponParam);
              }
            }, AUTO_CLOSE_TIMEOUT);

            trackFeature({
              eventType: 'impression',
              ...couponUnlockInfo.type.error.featureTracker,
            });

            break;
          }

          case 'silentError': {
            trackFeature({
              eventType: 'impression',
              ...couponUnlockInfo.type.silentError.featureTracker,
            });

            break;
          }
        }

        cancelTimeoutIdRef.current = setTimeout(() => {
          setIsOpen(false);
        }, AUTO_CLOSE_TIMEOUT);
      })
      .catch(() => {
        if (entryPageType === 'articleDetail') {
          showToast({
            type: 'WARNING',
            content: (
              <ErrorNotificationContent
                headline={t(
                  'Oops, we couldn’t apply your coupon. We’re sorry!',
                )}
              />
            ),
          });
        } else {
          setNextState({
            status: 'error',
            headline: t('Oops, we couldn’t apply your coupon. We’re sorry!'),
          });
        }

        // In case of CLP, for unknown errors, we close the modal after a timeout and redirect to the home page
        setTimeout(() => {
          setIsOpen(false);
          if (entryPageType === 'couponLandingPage') {
            const urlWithoutCrmCouponParam = getUrlWithParamsButCrmCoupon('/');

            history.replace(urlWithoutCrmCouponParam);
          }
        }, AUTO_CLOSE_TIMEOUT);
      });
  }, []);

  return {isOpen, couponActivationResponse};
};

const useGetUrlWithParamsButCrmCoupon = () => {
  const currentSearchParams = useSearch();

  return (url: string) => {
    const urlParams = new URL(url, location.href);
    const searchParams = new URLSearchParams(currentSearchParams);

    searchParams.forEach((value, key) => {
      urlParams.searchParams.set(key, value);
    });

    const searchWithoutCrmCoupon = updateUrlParams({
      search: urlParams.search,
      update: new Map([[CRM_COUPON_TOKEN_PARAM, undefined]]),
    });

    const queryString = searchWithoutCrmCoupon
      ? `?${searchWithoutCrmCoupon}`
      : searchWithoutCrmCoupon;

    return `${urlParams.pathname}${queryString}`;
  };
};

const useRemoveCrmCouponParam = () => {
  const pathname = usePathname();

  const searchWithoutCrmCoupon = useGetUrlWithParamsButCrmCoupon()(pathname);

  return () => {
    history.replaceState(null, '', searchWithoutCrmCoupon);
  };
};
